import { useMemo } from "react";
import styles from "./MarketViewer.module.css";

// Formatting utilities
const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatSales = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const scaled = num / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(scaled);
  } catch {
    return String(scaled);
  }
};

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  try {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return String(num);
  }
};

// Icons
const GlobeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const UsersIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const HomeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

// Components
const SectionHeader = ({ title }) => (
  <div className={styles.sectionHeaderBar}>
    <h2 className={styles.sectionHeaderText}>{title}</h2>
  </div>
);

const DefinitionRow = ({ label, value }) => (
  <div className={styles.definitionRow}>
    <div className={styles.definitionLabel}>{label}</div>
    <div className={styles.definitionValue}>{formatValue(value)}</div>
  </div>
);

const CircleChart = ({ label, value, type }) => {
  const circleClass = {
    tam: styles.circleTAM,
    sam: styles.circleSAM,
    som: styles.circleSOM,
  }[type];

  return (
    <div className={`${styles.circle} ${circleClass}`}>
      <span className={styles.circleLabel}>{label}</span>
      <span className={styles.circleValue}>{value}</span>
    </div>
  );
};

const USPChip = ({ text }) => <span className={styles.uspChip}>{text}</span>;

const ICPItem = ({ icon, value, label }) => {
  const IconSvg = icon;
  return (
    <div className={styles.icpItem}>
      <div className={styles.icpIcon}>
        <IconSvg />
      </div>
      <div className={styles.icpText}>
        <span className={styles.icpValue}>{value}</span>
        <span className={styles.icpLabel}>{label}</span>
      </div>
    </div>
  );
};

const IndicatorItem = ({ text, type }) => (
  <div className={styles.indicatorItem}>
    <div
      className={`${styles.indicatorDot} ${type === "red" ? styles.indicatorDotRed : styles.indicatorDotGreen}`}
    />
    <p className={styles.indicatorText}>{text}</p>
  </div>
);

const CheckItem = ({ text }) => (
  <div className={styles.checkItem}>
    <div className={`${styles.indicatorDot} ${styles.indicatorDotGreen}`} />
    <p className={styles.checkText}>{text}</p>
  </div>
);

const DomainLink = ({ domain }) => {
  const url = domain?.startsWith("http") ? domain : `https://${domain}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.domainLink}
    >
      {domain}
    </a>
  );
};

const MarketViewer = ({ data = {} }) => {
  const payload = data?.data || data;
  const summary =
    typeof payload?.summary === "string" ? payload.summary.trim() : "";
  const analysis = useMemo(() => {
    if (
      payload?.ai_analysis?.analysis &&
      typeof payload.ai_analysis.analysis === "object"
    ) {
      return payload.ai_analysis.analysis;
    }
    if (payload?.ai_analysis && typeof payload.ai_analysis === "object") {
      return payload.ai_analysis;
    }
    return {};
  }, [payload]);

  const marketDefinition = analysis?.market_definition_scope || {};
  const marketSize = analysis?.market_size_growth || {};
  const target = analysis?.target_audience || {};

  // Market Size Data
  const tamData = marketSize?.tam || {};
  const samData = marketSize?.sam || {};
  const somData = marketSize?.som || {};

  // Target Audience Data
  const niche = target?.niche || "";
  const brandPositioning = target?.brand_positioning || "";
  const usp = target?.usp || [];
  const icp = target?.icp || "";
  const ageRange = target?.age_range || "";
  const householdIncome = target?.household_income || "";
  const painPoints = target?.painpoints || [];
  const behavioralSignals = target?.behavioral_signals || [];
  const interestGraphs = target?.interest_graphs || [];

  return (
    <div className={styles.page}>
      {/* Dark Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <GlobeIcon />
        </div>
        <h1 className={styles.headerTitle}>Shop Market Analysis</h1>
      </div>

      <div className={styles.scrollContent}>
        <div className={styles.content}>
          {summary ? (
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Report Summary</p>
              <p className={styles.summaryText}>{summary}</p>
            </div>
          ) : null}
          {/* Market Definition Section */}
          <section className={styles.section}>
            <SectionHeader title="Market Definition" />
            <div className={styles.definitionTable}>
              <DefinitionRow
                label="Market Type"
                value={marketDefinition?.market_type}
              />
              <DefinitionRow
                label="Geography"
                value={marketDefinition?.geography}
              />
              <DefinitionRow
                label="Business Model"
                value={marketDefinition?.business_model}
              />
              <DefinitionRow
                label="Customer Type"
                value={marketDefinition?.customer_type}
              />
              <DefinitionRow
                label="Price Segment"
                value={marketDefinition?.price_segment}
              />
            </div>
          </section>

          {/* Market Size & Growth Section */}
          <section className={styles.section}>
            <SectionHeader title="Market Size & Growth" />
            <div className={styles.marketSizeContainer}>
              {/* Left Side - Descriptions */}
              <div className={styles.marketSizeLeft}>
                <div className={styles.marketSizeItem}>
                  <h4 className={styles.marketSizeItemTitle}>TAM</h4>
                  <p className={styles.marketSizeItemDesc}>
                    {tamData?.description ||
                      `${formatCount(tamData?.store_count)} active stores in your broader market`}
                  </p>
                </div>
                <div className={styles.marketSizeItem}>
                  <h4 className={styles.marketSizeItemTitle}>SAM</h4>
                  <p className={styles.marketSizeItemDesc}>
                    {samData?.description ||
                      `Across ${formatCount(samData?.store_count)} closer-fit stores`}
                  </p>
                </div>
                <div className={styles.marketSizeItem}>
                  <h4 className={styles.marketSizeItemTitle}>SOM</h4>
                  <p className={styles.marketSizeItemDesc}>
                    {somData?.description ||
                      `Top direct competitors by niche keywords across ${formatCount(somData?.store_count)} stores`}
                  </p>
                </div>
                {tamData?.top_domains?.length > 0 && (
                  <div className={styles.topEarners}>
                    <h5 className={styles.topEarnersLabel}>Top Earners</h5>
                    <div className={styles.topEarnersLinks}>
                      {tamData.top_domains.map((domain, idx) => (
                        <DomainLink key={idx} domain={domain} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Circles */}
              <div className={styles.circlesContainer}>
                <CircleChart
                  label="TAM"
                  value={formatSales(tamData?.total_sales_yearly)}
                  type="tam"
                />
                <CircleChart
                  label="SAM"
                  value={formatSales(samData?.total_sales_yearly)}
                  type="sam"
                />
                <CircleChart
                  label="SOM"
                  value={formatSales(somData?.total_sales_yearly)}
                  type="som"
                />
              </div>
            </div>
          </section>
          <SectionHeader title="Target Audience" />
          <div className={styles.targetAudienceContainer}>
            <section className={styles.section}>
              {/* Niche */}
              {niche && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Niche</h3>
                  <p className={styles.subsectionContent}>{niche}</p>
                </div>
              )}

              {/* Brand Positioning */}
              {brandPositioning && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>
                    Brand Positioning
                  </h3>
                  <p className={styles.subsectionContent}>{brandPositioning}</p>
                </div>
              )}

              {/* USP */}
              {usp.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>USP</h3>
                  <div className={styles.uspContainer}>
                    {usp.map((item, idx) => (
                      <USPChip key={idx} text={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* Ideal Customer Profile */}
              <div className={styles.audienceSubsection}>
                <h3 className={styles.marketSizeItemTitle}>
                  Ideal Customer Profile
                </h3>
                {icp && (
                  <p
                    className={styles.subsectionContent}
                    style={{ marginBottom: "16px" }}
                  >
                    {icp}
                  </p>
                )}
                <div className={styles.icpContainer}>
                  {ageRange && (
                    <ICPItem
                      icon={UsersIcon}
                      value={ageRange}
                      label="Age Range"
                    />
                  )}
                  {householdIncome && (
                    <ICPItem
                      icon={HomeIcon}
                      value={householdIncome}
                      label="Household income"
                    />
                  )}
                </div>
              </div>

              {/* Pain Points */}
              {painPoints.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Pain Points</h3>
                  <div className={styles.indicatorList}>
                    {painPoints.map((point, idx) => (
                      <IndicatorItem key={idx} text={point} type="red" />
                    ))}
                  </div>
                </div>
              )}

              {/* Behavioral & Interest Signals */}
              <div className={styles.signalsGrid}>
                {/* Behavioral Signals */}
                {behavioralSignals.length > 0 && (
                  <div className={styles.signalSection}>
                    <h4 className={styles.marketSizeItemTitle}>
                      Behavioral Signals
                    </h4>
                    <div className={styles.checkList}>
                      {behavioralSignals.map((signal, idx) => (
                        <CheckItem key={idx} text={signal} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Interest Signals */}
                {interestGraphs.length > 0 && (
                  <div className={styles.signalSection}>
                    <h4 className={styles.marketSizeItemTitle}>
                      Interests Signals
                    </h4>
                    <div className={styles.checkList}>
                      {interestGraphs.map((interest, idx) => (
                        <CheckItem key={idx} text={interest} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Target Audience List (if array) */}
              {/* {Array.isArray(targetAudience) && targetAudience.length > 0 && (
              <div className={styles.audienceSubsection}>
                <h3 className={styles.marketSizeItemTitle}>Target Audience</h3>
                <div className={styles.checkList}>
                  {targetAudience.map((item, idx) => (
                    <CheckItem key={idx} text={item} />
                  ))}
                </div>
              </div>
            )} */}
            </section>
          </div>
          {/* Target Audience Section */}

          {/* Adjacent Analysis Section */}
          {/* {(adjacent?.summary || adjacent?.opportunities?.length > 0) && (
            <section className={styles.section}>
              <SectionHeader title="Adjacent Analysis" />

              {adjacent?.summary && (
                <div className={styles.audienceSubsection}>
                  <p className={styles.subsectionContent}>
                    {Array.isArray(adjacent.summary)
                      ? adjacent.summary.join(", ")
                      : adjacent.summary}
                  </p>
                </div>
              )}

              {adjacent?.opportunities?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Opportunities</h3>
                  <div className={styles.checkList}>
                    {adjacent.opportunities.map((opp, idx) => (
                      <CheckItem key={idx} text={opp} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )} */}

          {/* Competitor Analysis Section */}
          {/* {(competitor?.summary || competitor?.key_competitors?.length > 0) && (
            <section className={styles.section}>
              <SectionHeader title="Competitor Analysis" />

              {competitor?.summary && (
                <div className={styles.audienceSubsection}>
                  <p className={styles.subsectionContent}>
                    {competitor.summary}
                  </p>
                </div>
              )}

              {competitor?.key_competitors?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  {competitor.key_competitors.slice(0, 3).map((comp, idx) => (
                    <div
                      key={idx}
                      className={styles.marketSizeItem}
                      style={{ marginBottom: "16px" }}
                    >
                      <h4 className={styles.marketSizeItemTitle}>
                        {comp?.shop_name || comp?.domain || "Unknown"}
                      </h4>
                      {comp?.domain && (
                        <p className={styles.marketSizeItemDesc}>
                          {comp.domain}
                        </p>
                      )}
                      {comp?.technical_maturity && (
                        <span
                          className={styles.uspChip}
                          style={{ marginTop: "8px", display: "inline-block" }}
                        >
                          {comp.technical_maturity}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {competitor?.opportunities?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Opportunities</h3>
                  <div className={styles.checkList}>
                    {competitor.opportunities.map((opp, idx) => (
                      <CheckItem key={idx} text={opp} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )} */}

          {/* International Inspiration Section */}
          {/* {(inspiration?.summary || inspiration?.top_shops?.length > 0) && (
            <section className={styles.section}>
              <SectionHeader title="International Inspiration" />

              {inspiration?.summary && (
                <div className={styles.audienceSubsection}>
                  <p className={styles.subsectionContent}>
                    {inspiration.summary}
                  </p>
                </div>
              )}

              {inspiration?.top_shops?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  {inspiration.top_shops.map((shopEntry, idx) => (
                    <div
                      key={idx}
                      className={styles.marketSizeItem}
                      style={{ marginBottom: "16px" }}
                    >
                      <h4 className={styles.marketSizeItemTitle}>
                        {shopEntry?.shop_name || "Unknown"}
                      </h4>
                      {shopEntry?.domain && (
                        <p className={styles.marketSizeItemDesc}>
                          {shopEntry.domain}
                        </p>
                      )}
                      {shopEntry?.website_positioning_theme && (
                        <p
                          className={styles.subsectionContent}
                          style={{ marginTop: "8px" }}
                        >
                          {shopEntry.website_positioning_theme}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {inspiration?.key_takeaways?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Key Takeaways</h3>
                  <div className={styles.checkList}>
                    {inspiration.key_takeaways.map((takeaway, idx) => (
                      <CheckItem key={idx} text={takeaway} />
                    ))}
                  </div>
                </div>
              )}

              {inspiration?.opportunities?.length > 0 && (
                <div className={styles.audienceSubsection}>
                  <h3 className={styles.marketSizeItemTitle}>Opportunities</h3>
                  <div className={styles.checkList}>
                    {inspiration.opportunities.map((opp, idx) => (
                      <CheckItem key={idx} text={opp} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default MarketViewer;
